import * as React from "react";
import { Control, useFieldArray, useWatch } from "react-hook-form";
import {
  Text,
  Grid,
  Title,
  Button,
  ActionIcon,
  Modal,
  Group,
  Divider,
  useMantineTheme,
  Stack,
  Flex,
} from "@mantine/core";

import {
  CCheckbox,
  CMultiSelect,
  CMultiSelectCreatable,
  CRadioGroup,
  CSelect,
  CTextarea,
  CTextInput,
} from "@supportinitiative/react-hook-form-mantine";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import { UseCaseCVMContext } from "./UseCaseCVMContext";

import MasterDatasetCVM from "./Dataset/MasterDatasetCVM";
import { columnTypes } from "./UseCaseUtils";
import { getId } from "../utilities";
import { useLoaders } from "../hooks";

function PreferencesCVM({
  name,
  control,
  userStatus,
  setUserStatus,
}: {
  name: `definition`;
  control: Control<any>;
  userStatus: {
    label: string;
    value: string;
  }[];
  setUserStatus: any;
}) {
  const { state: useCaseFormState, dispatch: reconsetFormDispatch } = React.useContext(UseCaseCVMContext);

  const theme = useMantineTheme();

  const inputsArray = useFieldArray({
    control,
    name: `${name}.inputs`,
    keyName: "key",
  });

  const { data: loaders } = useLoaders(useCaseFormState.entityId);

  return (
    <Stack
      style={{
        marginTop: theme.spacing.xs,
        padding: theme.spacing.xs,
        width: "100%",
      }}
    >
      <Stack gap="0">
        <Flex>
          <Title
            mb="xs"
            order={5}
            style={{
              padding: "0px 5px",
              backgroundColor: "#16A596",
              color: "#fff",
            }}
          >
            Master Dataset
          </Title>
        </Flex>
        <Grid gutter={"xs"} mb="xs">
          <Grid.Col span={2}>
            <MasterDatasetCVM control={control} name="definition.masterDataset" />
          </Grid.Col>
        </Grid>
      </Stack>

      <Stack gap="0">
        <Flex>
          <Title
            mb="xs"
            mt="xs"
            order={5}
            style={{
              padding: "0px 5px",
              backgroundColor: "#16A596",
              color: "#fff",
            }}
          >
            User Status
          </Title>
        </Flex>

        <Grid align="flex-start" gutter={"xs"} mb="xs" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={4}>
            <CMultiSelectCreatable
              name={`definition.userStatus`}
              placeholder="User Status"
              control={control}
              data={userStatus}
              disabled={useCaseFormState.actionOptions.disableAll}
              onCreate={(values) => {
                // const newUserStatus = [...userStatus, ...values];
                // union userStatus and values
                const newStatus = [...values, ...userStatus].map((value: any) => value.value);
                const uniqueStatus = [...new Set(newStatus)];

                setUserStatus(
                  uniqueStatus.map((value: any) => {
                    return { label: value, value: value };
                  }),
                );
                reconsetFormDispatch({
                  type: "SET-USER-STATUS",
                  value: uniqueStatus.map((value: any) => {
                    return { label: value, value: value };
                  }),
                });
              }}
            />
          </Grid.Col>
        </Grid>
      </Stack>

      <Stack gap="0">
        <Flex>
          <Title
            mb="xs"
            mt="xs"
            order={5}
            style={{
              padding: "0px 5px",
              backgroundColor: "#16A596",
              color: "#fff",
            }}
          >
            Loader
          </Title>
        </Flex>

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={4}>
            <CSelect
              name={`definition.loaderId`}
              placeholder="Loader"
              control={control}
              data={
                loaders && loaders.length > 0
                  ? loaders.map((loader: any) => {
                      return { label: loader.name, value: loader.id };
                    })
                  : []
              }
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
      </Stack>

      <Stack gap="0">
        <Group mb="xs" align="center" style={{ alignItems: "center" }}>
          <Flex>
            <Title
              m={0}
              order={5}
              style={{
                padding: "0px 5px",
                backgroundColor: "#16A596",
                color: "#fff",
              }}
            >
              Inputs
            </Title>
          </Flex>
          <ActionIcon
            size="sm"
            variant="white"
            color="gray"
            onClick={() => {
              inputsArray.append({
                name: "",
                type: "TEXT",
                label: "",
                notNull: "NO",
                id: getId(),
              });
            }}
            style={{
              display: useCaseFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
            }}
            disabled={useCaseFormState.actionOptions.disableAll}
          >
            <IconPlus />
          </ActionIcon>
        </Group>

        {inputsArray.fields.length === 0 ? (
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={4}>
              <Divider label="No inputs" labelPosition="left" />
            </Grid.Col>
          </Grid>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Grid align="stretch" gutter={"xs"}>
              <Grid.Col span={12}>
                {inputsArray.fields.map((field, index) => {
                  return (
                    <Grid align="stretch" gutter={"xs"} mb="xs" key={field.key}>
                      <Grid.Col span={2}>
                        <CTextInput
                          name={`${name}.inputs.${index}.name`}
                          control={control}
                          showErrorText={false}
                          label={index === 0 ? "Name" : ""}
                          required
                          disabled={useCaseFormState.actionOptions.disableAll}
                        />
                      </Grid.Col>
                      <Grid.Col span={2}>
                        <CSelect
                          name={`${name}.inputs.${index}.type`}
                          control={control}
                          label={index === 0 ? "Type" : ""}
                          data={columnTypes}
                          showErrorText={false}
                          required
                          disabled={useCaseFormState.actionOptions.disableAll}
                        />
                      </Grid.Col>
                      <Grid.Col span={2}>
                        <CTextInput
                          name={`${name}.inputs.${index}.label`}
                          control={control}
                          showErrorText={false}
                          required
                          label={index === 0 ? "Label" : ""}
                          disabled={useCaseFormState.actionOptions.disableAll}
                        />
                      </Grid.Col>

                      <Grid.Col span={2} style={{ display: "flex", alignItems: "flex-end" }}>
                        <CCheckbox
                          name={`${name}.inputs.${index}.notNull`}
                          control={control}
                          label="Not Null"
                          trueValue="YES"
                          falseValue="NO"
                          disabled={useCaseFormState.actionOptions.disableAll}
                        />
                      </Grid.Col>

                      <Grid.Col
                        span={1}
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                        }}
                      >
                        <ActionIcon
                          size="sm"
                          variant="white"
                          color="gray"
                          onClick={() => {
                            inputsArray.remove(index);
                          }}
                          disabled={useCaseFormState.actionOptions.disableAll}
                          style={{
                            display: useCaseFormState.actionOptions.disableAll ? "none" : "",
                          }}
                        >
                          <IconTrash />
                        </ActionIcon>
                      </Grid.Col>
                    </Grid>
                  );
                })}
              </Grid.Col>
            </Grid>
          </div>
        )}
      </Stack>

      <Stack gap="0">
        <Flex>
          <Title
            mb="xs"
            mt="xs"
            order={5}
            style={{
              padding: "0px 5px",
              backgroundColor: "#16A596",
              color: "#fff",
            }}
          >
            Run
          </Title>
        </Flex>

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={4}>
            <CRadioGroup
              name={`${name}.preferences.run`}
              control={control}
              data={[
                { label: "Allow multiple active run", value: "MULTIPLE" },
                { label: "Allow continuous run", value: "CONTINUOUS" },
                { label: "Allow only one run per master", value: "ONCE" },
              ]}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <CRadioGroup
              name={`${name}.preferences.match`}
              control={control}
              data={[
                { label: "Simple", value: "SIMPLE" },
                { label: "Multiple", value: "MULTIPLE" },
                { label: "Hierarchical", value: "HIERARCHICAL" },
              ]}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <CRadioGroup
              name={`${name}.preferences.runManagement`}
              control={control}
              data={[
                { label: "Automatic", value: "AUTOMATIC" },
                { label: "Semi Automatic", value: "SEMI_AUTOMATIC" },
                { label: "Manual", value: "MANUAL" },
              ]}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
      </Stack>
    </Stack>
  );
}

export default PreferencesCVM;
